import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useChangeFacilityUserRole } from '@/api/hooks/users/useChangeFacilityUserRole';
import { useGetRolesForForms } from '@/api/hooks/roles/useGetRolesForForms';
import CpdActionButton from '@/components/ui/CpdActionButton';
import DefaultRoleBadge from '@/components/DefaultRoleBadge/DefaultRoleBadge';
import AllFacilitiesAccessBadge from '@/components/AllFacilitiesAccessBadge/AllFacilitiesAccessBadge';

const RoleChangeDialog = ({ open, onClose, user, onRoleChanged }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState('');
  const {
    changeRole,
    loading: changeRoleLoading,
  } = useChangeFacilityUserRole();
  const { roles, loading: rolesLoading } = useGetRolesForForms();

  const isCustomPermissions =
    !user?.role_id || user?.role_name === 'Custom Permissions';

  useEffect(() => {
    if (open && user) {
      setSelectedRole(user.role_id || '');
    }
  }, [open, user]);

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error(t('internalUsers.roleDialog.selectRoleError'));
      return;
    }

    if (selectedRole === user.role_id) {
      toast.info(t('internalUsers.roleDialog.noChangeError'));
      onClose();
      return;
    }

    try {
      await changeRole(user.id, selectedRole);
      onRoleChanged();
      onClose();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const handleClose = () => {
    if (!changeRoleLoading) {
      onClose();
    }
  };

  const currentRoleName = user?.role_name || 'Custom Permissions';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Typography variant='h6'>
            {t('internalUsers.roleDialog.title')}
          </Typography>
          <Tooltip title={t('common.close')}>
            <IconButton
              onClick={handleClose}
              size='small'
              disabled={changeRoleLoading}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant='body1' gutterBottom>
            <strong>{t('internalUsers.roleDialog.user')}:</strong> {user?.name}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {user?.email}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              <strong>{t('internalUsers.roleDialog.currentRole')}:</strong>{' '}
              {currentRoleName}
            </Typography>
            <DefaultRoleBadge role={{ is_default: user?.role_is_default }} />
            <AllFacilitiesAccessBadge
              role={{
                has_all_facilities_access: user?.has_all_facilities_access,
              }}
            />
          </Box>
        </Box>

        {isCustomPermissions ? (
          <Alert severity='info'>
            <Typography variant='body2'>
              If you would like to change permissions on this user please reach
              out to hello@clientpaydirect.com for support.
            </Typography>
          </Alert>
        ) : (
          <>
            <FormControl fullWidth size='small'>
              <InputLabel id='role-select-label'>
                {t('internalUsers.roleDialog.selectRole')}
              </InputLabel>
              <Select
                labelId='role-select-label'
                id='role-select'
                value={selectedRole}
                label={t('internalUsers.roleDialog.selectRole')}
                onChange={handleRoleChange}
                disabled={changeRoleLoading || rolesLoading}
                size='small'>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body1'>{role.name}</Typography>
                      <DefaultRoleBadge role={role} />
                      <AllFacilitiesAccessBadge role={role} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRole && selectedRole !== user?.role_id && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                <Typography variant='body2'>
                  {t('internalUsers.roleDialog.changeWarning')}
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={changeRoleLoading}>
          {t('common.close')}
        </Button>
        {!isCustomPermissions && (
          <CpdActionButton
            onClick={handleSubmit}
            disabled={
              changeRoleLoading ||
              !selectedRole ||
              selectedRole === user?.role_id
            }
            loading={changeRoleLoading}
            startIcon={<Save sx={{ color: 'white' }} />}>
            {t('common.save')} Changes
          </CpdActionButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RoleChangeDialog;
